import React, { useState, useRef, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas'; // Keep html2canvas for fallback mechanism
import { 
  Box, 
  Button, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  CircularProgress, 
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { API_URL } from '../config';
import FeedbackAnnotation from './FeedbackAnnotation';
import { useLocation } from 'react-router-dom';

// Function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Color options for annotations
const colorOptions = [
  { color: 'red', label: 'Red' },
  { color: 'blue', label: 'Blue' },
  { color: 'green', label: 'Green' },
  { color: 'yellow', label: 'Yellow' },
  { color: 'black', label: 'Black' }
];

const FeedbackComponent = ({ targetRef, conversationId }) => {
  const query = useQuery();
  const urlConversationId = query.get('conversationId');
  // Use conversationId from props or URL parameter
  const effectiveConversationId = conversationId || urlConversationId;
  
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCapturingWithDialogClosed, setIsCapturingWithDialogClosed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [includeSystemInfo, setIncludeSystemInfo] = useState(true);
  const [activeColor, setActiveColor] = useState('red');
  const annotationRef = useRef();
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false);
  const [isAnnotated, setIsAnnotated] = useState(false);
  const [memberId, setMemberId] = useState(null);

  // Get member_id from localStorage when component mounts
  useEffect(() => {
    const getMemberId = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user && user.id) {
            setMemberId(user.id);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };

    getMemberId();
  }, []);

  // Modify handleOpenFeedback to reset the isCapturingWithDialogClosed state
  const handleOpenFeedback = () => {
    setOpen(true);
    setIsCapturingWithDialogClosed(false);
    console.log('Debug - Conversation ID in feedback component:', effectiveConversationId);
  };

  // Modify handleCloseFeedback to handle the case when capturing is in progress
  const handleCloseFeedback = () => {
    // Prevent closing if we're in the middle of capturing with dialog closed
    if (isCapturingWithDialogClosed) {
      return;
    }
    setOpen(false);
    setScreenshot(null);
    setFeedbackText('');
    setActiveColor('red');
    setIsAnnotated(false);
  };

  // Add a function to show a temporary capture notification when dialog is closed
  const showCaptureNotification = useCallback(() => {
    if (isCapturingWithDialogClosed) {
      setSnackbarSeverity('info');
      setSnackbarMessage('Please select the area to capture for your feedback');
      setSnackbarOpen(true);
    }
  }, [isCapturingWithDialogClosed]);

  // Use effect to show notification when isCapturingWithDialogClosed changes
  useEffect(() => {
    if (isCapturingWithDialogClosed) {
      showCaptureNotification();
    }
  }, [isCapturingWithDialogClosed, showCaptureNotification]);

  // Capture screenshot
  const captureScreenshot = useCallback(async () => {
    try {
      setIsCapturing(true);
      
      // Temporarily close the dialog before taking screenshot
      setIsCapturingWithDialogClosed(true);
      setOpen(false);
      
      // Short delay to ensure the dialog is fully closed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if the browser supports Screen Capture API
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        try {
          // Use Chrome's Screen Capture API to get the screen content
          const stream = await navigator.mediaDevices.getDisplayMedia({
            preferCurrentTab: true,
            video: {
              displaySurface: "browser",
            }
          }).catch(error => {
            // This will catch user cancellation
            console.log('Screen capture was cancelled by user');
            // Reopen the feedback dialog
            setOpen(true);
            setIsCapturingWithDialogClosed(false);
            setIsCapturing(false);
            // Rethrow to stop further processing
            throw error;
          });
          
          // Create a video element to capture a frame
          const video = document.createElement('video');
          video.srcObject = stream;
          
          // Wait for the video to have enough data to capture a frame
          await new Promise((resolve) => {
            video.onloadedmetadata = () => {
              video.play();
              resolve();
            };
          });
          
          // Wait a short moment to ensure the first frame is fully loaded
          await new Promise((resolve) => setTimeout(resolve, 200));
          
          // Create a canvas and draw the video frame to it
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Stop all tracks to end screen sharing
          stream.getTracks().forEach(track => track.stop());
          
          // Scale the screenshot to 50% using a temporary canvas
          const originalWidth = canvas.width;
          const originalHeight = canvas.height;
          const scaledWidth = Math.floor(originalWidth * 0.5);
          const scaledHeight = Math.floor(originalHeight * 0.5);
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = scaledWidth;
          tempCanvas.height = scaledHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(canvas, 0, 0, originalWidth, originalHeight, 0, 0, scaledWidth, scaledHeight);
          
          // Convert to data URL and set state
          const screenshotUrl = tempCanvas.toDataURL('image/png');
          setScreenshot(screenshotUrl);
          
          // Finally, reopen the feedback dialog with the screenshot
          setIsCapturing(false);
          setActiveColor('red');
          setIsAnnotated(false);
          setOpen(true);
          setIsCapturingWithDialogClosed(false);
          return;
        } catch (error) {
          console.warn('Screen Capture API failed, falling back to html2canvas:', error);
          // Stop any active tracks if there was a stream created
          if (error.stream && error.stream.getTracks) {
            error.stream.getTracks().forEach(track => track.stop());
          }
          // Reopen the dialog before continuing to fallback
          setOpen(true);
          setIsCapturingWithDialogClosed(false);
          // Continue to html2canvas fallback
        }
      }
      
      // Reopen the dialog if we're going to use html2canvas
      setOpen(true);
      setIsCapturingWithDialogClosed(false);
      
      // Fallback to html2canvas if Screen Capture API is not supported or failed
      if (!targetRef.current) {
        throw new Error('Target reference is not available');
      }
      
      // Save the original styles
      const originalOverflow = targetRef.current.style.overflow;
      const originalHeight = targetRef.current.style.height;
      const originalPosition = targetRef.current.style.position;
      
      // Temporarily modify styles for better capture
      targetRef.current.style.overflow = 'visible';
      targetRef.current.style.height = 'auto';
      
      // Capture the full area using html2canvas
      const htmlCanvas = await html2canvas(targetRef.current, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        scale: window.devicePixelRatio,
        windowWidth: document.documentElement.offsetWidth,
        windowHeight: document.documentElement.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        width: targetRef.current.scrollWidth,
        height: targetRef.current.scrollHeight,
        ignoreElements: (element) => {
          // Ignore feedback button and dialog
          return element.classList.contains('feedback-component') || 
                 element.closest('.feedback-dialog') !== null;
        }
      });
      
      // Restore original styles
      targetRef.current.style.overflow = originalOverflow;
      targetRef.current.style.height = originalHeight;
      targetRef.current.style.position = originalPosition;
      
      // Scale the screenshot to 50% using a temporary canvas
      const htmlCanvasWidth = htmlCanvas.width;
      const htmlCanvasHeight = htmlCanvas.height;
      const htmlScaledWidth = Math.floor(htmlCanvasWidth * 0.5);
      const htmlScaledHeight = Math.floor(htmlCanvasHeight * 0.5);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = htmlScaledWidth;
      tempCanvas.height = htmlScaledHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(htmlCanvas, 0, 0, htmlCanvasWidth, htmlCanvasHeight, 0, 0, htmlScaledWidth, htmlScaledHeight);
      
      // Convert to data URL and set state
      const screenshotUrl = tempCanvas.toDataURL('image/png');
      setScreenshot(screenshotUrl);
      setIsCapturing(false);
      setActiveColor('red');
      setIsAnnotated(false);
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      // Make sure to reopen the dialog if there was an error
      if (isCapturingWithDialogClosed) {
        setOpen(true);
        setIsCapturingWithDialogClosed(false);
      }
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to capture screenshot');
      setSnackbarOpen(true);
      setIsCapturing(false);
    }
  }, [targetRef, isCapturingWithDialogClosed]);

  // Submit feedback with screenshot
  const handleSubmitFeedback = async (event) => {
    if (event) event.preventDefault();
    
    if (!screenshot && !feedbackText) {
      setSnackbarSeverity('warning');
      setSnackbarMessage('Please provide feedback or a screenshot');
      setSnackbarOpen(true);
      return;
    }
    try {
      setIsSubmitting(true);
      let finalScreenshot = screenshot;
      if (isAnnotated && screenshot && annotationRef.current && annotationRef.current.save) {
        try {
          console.log('Getting final annotated screenshot for submission');
          finalScreenshot = await annotationRef.current.save();
          if (!finalScreenshot) {
            console.warn('Failed to get annotated screenshot, using current screenshot');
            finalScreenshot = screenshot;
          }
        } catch (error) {
          console.error('Error saving annotated screenshot:', error);
        }
      }
      const systemInfo = includeSystemInfo ? {
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        url: window.location.href,
        date: new Date().toISOString()
      } : null;

      // Get current member ID if available
      const userData = localStorage.getItem('user');
      let currentMemberId = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          currentMemberId = user.id;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      // Process screenshot to make it smaller if needed
      let screenshotB64 = null;
      if (finalScreenshot) {
        try {
          // If it's already a base64 string, extract the data part
          if (finalScreenshot.startsWith('data:image')) {
            // Extract image format and data
            const matches = finalScreenshot.match(/^data:image\/([a-zA-Z]+);base64,(.+)$/);
            
            if (matches && matches.length === 3) {
              const imageFormat = matches[1];
              const base64Data = matches[2];
              
              // For very large screenshots, we'll compress them
              if (base64Data.length > 1000000) { // If larger than ~1MB
                console.log('Large screenshot detected, compressing...');
                // Use a temp canvas to resize and compress
                const img = new Image();
                await new Promise(resolve => {
                  img.onload = resolve;
                  img.src = finalScreenshot;
                });
                
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Calculate new dimensions - reduce to 50% if large
                const maxWidth = img.width > 1200 ? img.width / 2 : img.width;
                const maxHeight = img.height > 1200 ? img.height / 2 : img.height;
                
                canvas.width = maxWidth;
                canvas.height = maxHeight;
                
                // Draw resized image and get reduced quality JPEG
                ctx.drawImage(img, 0, 0, maxWidth, maxHeight);
                const compressedScreenshot = canvas.toDataURL('image/jpeg', 0.7);
                
                // Extract just the base64 data
                screenshotB64 = compressedScreenshot.split(',')[1];
              } else {
                screenshotB64 = base64Data;
              }
            } else {
              console.warn('Invalid data URL format for screenshot');
              screenshotB64 = finalScreenshot.split(',')[1]; // Basic fallback
            }
          } else {
            // Otherwise, just use the string as is
            screenshotB64 = finalScreenshot;
          }
        } catch (error) {
          console.error('Error processing screenshot:', error);
          // Continue without screenshot if processing fails
          screenshotB64 = null;
        }
      }

      // Store feedback through the VM API; database credentials never reach the browser.
      try {
        await axios.post(`${API_URL}/api/storage/feedback`, {
          member_id: currentMemberId ? parseInt(currentMemberId) : null,
          conversation_id: effectiveConversationId ? parseInt(effectiveConversationId) : null,
          feedback_text: feedbackText || '',
          screenshot_path: screenshotB64,
          system_info: systemInfo
        });
        
        setIsSubmitting(false);
        setSnackbarSeverity('success');
        setSnackbarMessage('Feedback submitted successfully!');
        setSnackbarOpen(true);
        handleCloseFeedback();
      } catch (dbError) {
        console.error('Database error when submitting feedback:', dbError);
        
        // If the screenshot is too large for the database, try submitting without it
        if (screenshotB64 && (dbError.message.includes('too large') || dbError.message.includes('exceeded') || dbError.message.includes('size'))) {
          console.log('Screenshot may be too large, attempting to submit without it');
          
          await axios.post(`${API_URL}/api/storage/feedback`, {
            member_id: currentMemberId ? parseInt(currentMemberId) : null,
            conversation_id: effectiveConversationId ? parseInt(effectiveConversationId) : null,
            feedback_text: `${feedbackText || ''} [Screenshot too large to store]`.trim(),
            screenshot_path: null,
            system_info: systemInfo
          });
          
          setIsSubmitting(false);
          setSnackbarSeverity('success');
          setSnackbarMessage('Feedback submitted successfully (without screenshot)');
          setSnackbarOpen(true);
          handleCloseFeedback();
        } else {
          throw dbError; // Re-throw for general error handling
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setIsSubmitting(false);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to submit feedback');
      setSnackbarOpen(true);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Handle closing the annotation dialog
  const handleCloseAnnotationDialog = async () => {
    // Save the annotated screenshot with rectangles
    if (annotationRef.current && annotationRef.current.save) {
      try {
        console.log('Saving annotations...');
        const annotatedScreenshot = await annotationRef.current.save();
        console.log('Annotations saved, updating screenshot...');
        
        if (annotatedScreenshot) {
          // Update the screenshot state with the annotated version
          setScreenshot(annotatedScreenshot);
          setIsAnnotated(true); // Mark as annotated
          console.log('Screenshot updated with annotations');
        }
      } catch (error) {
        console.error('Error saving annotation:', error);
      }
    }
    setAnnotationDialogOpen(false);
  };

  // Handler for when annotations are saved
  const handleSaveAnnotation = (annotatedScreenshot) => {
    try {
      console.log('Annotations saved explicitly');
      if (annotatedScreenshot) {
        setScreenshot(annotatedScreenshot);
        setIsAnnotated(true);
        setSnackbarSeverity('success');
        setSnackbarMessage('Annotations saved successfully');
        setSnackbarOpen(true);
      } else {
        throw new Error('No screenshot data received');
      }
    } catch (error) {
      console.error('Error in handleSaveAnnotation:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to save annotations');
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      {/* Feedback button */}
      <Tooltip title="Send Feedback">
        <IconButton 
          onClick={handleOpenFeedback}
          color="primary"
          className="feedback-component"
          sx={{ 
            position: 'fixed', 
            bottom: '20px', 
            right: '20px',
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            boxShadow: 3
          }}
        >
          <FeedbackIcon />
        </IconButton>
      </Tooltip>
      {/* Feedback dialog */}
      <Dialog 
        open={open} 
        onClose={isCapturingWithDialogClosed ? () => {} : handleCloseFeedback}
        fullWidth
        maxWidth="lg"
        className="feedback-dialog"
        sx={{
          '& .MuiDialog-paper': {
            width: '90vw',
            maxWidth: '1200px',
            height: '90vh',
            maxHeight: '900px'
          }
        }}
      >
        <DialogTitle>
          Send Feedback
          <IconButton
            aria-label="close"
            onClick={handleCloseFeedback}
            disabled={isCapturingWithDialogClosed || isCapturing}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => isCapturingWithDialogClosed || isCapturing ? theme.palette.grey[400] : theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Please tell us about your experience or report an issue
            </Typography>
            <TextField
              label="Describe your feedback"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              margin="normal"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSystemInfo}
                  onChange={(e) => setIncludeSystemInfo(e.target.checked)}
                  color="primary"
                />
              }
              label="Include system information"
            />
          </Box>
          <Box sx={{ mb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <Typography variant="subtitle1" gutterBottom>
              Screenshot
            </Typography>
            {!screenshot ? (
              <Box 
                sx={{ 
                  border: '1px dashed grey',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This dialog will temporarily close while you select the area to capture. 
                  The dialog will reopen automatically after the screenshot is taken.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={captureScreenshot}
                  startIcon={<PhotoCameraIcon />}
                  disabled={isCapturing}
                >
                  {isCapturing ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Capturing...
                    </>
                  ) : (
                    'Take Screenshot'
                  )}
                </Button>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', pt: 2 }}>
                {/* Thumbnail preview, click to open fullscreen annotation */}
                <Box sx={{ cursor: 'pointer', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden', maxWidth: 320, maxHeight: 180 }} onClick={() => setAnnotationDialogOpen(true)}>
                  <img 
                    src={screenshot} 
                    alt="Screenshot preview" 
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    key={isAnnotated ? 'annotated-thumbnail' : 'original-thumbnail'} 
                  />
                </Box>
                <Button onClick={() => setAnnotationDialogOpen(true)} sx={{ ml: 2 }} variant="outlined">
                  {isAnnotated ? 'Edit Annotations' : 'Annotate Fullscreen'}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedback}>Cancel</Button>
          <Button 
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSubmitFeedback}
            disabled={isSubmitting || (!screenshot && !feedbackText)}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              'Send Feedback'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Fullscreen annotation dialog */}
      <Dialog 
        open={annotationDialogOpen} 
        onClose={handleCloseAnnotationDialog} 
        fullScreen
      >
        <DialogTitle>
          Annotate Screenshot
          <IconButton
            aria-label="close"
            onClick={handleCloseAnnotationDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, width: '100vw', height: '100vh', boxSizing: 'border-box' }}>
          <Box sx={{ width: '100vw', height: 'calc(100vh - 64px)', overflow: 'auto', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
            <FeedbackAnnotation
              ref={annotationRef}
              screenshot={screenshot}
              activeColor={activeColor}
              setActiveColor={setActiveColor}
              onRetake={captureScreenshot}
              colorOptions={colorOptions}
              onSave={handleSaveAnnotation}
            />
          </Box>
        </DialogContent>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackComponent;
